declare const XLSX: any
declare const pako: any

import { doc, setDoc } from 'firebase/firestore'
import { ref, uploadBytesResumable } from 'firebase/storage'
import { db, storage } from '@/core/firebase'
import { currentUser } from '@/core/state'
import { getAccountContext, getUserBranches } from '@/store/api'
import { getPeriodRangeFromEsbData, getPeriodRangeFromMokaData, getPeriodFromSalesData, detectBranchNameFromSheet } from './utils'
import { listenForProcessingStatus } from './processing'
import { uploadStore } from './store'
import type { WorkSheet } from 'xlsx'

/**
 * Handles uploading of a SINGLE sales data file.
 * AUTO-DETECTS branch from file content.
 */
export async function handleSalesDataUpload(file: File, format: 'ESB' | 'MOKA', _ignoredBranchId?: string, _ignoredBranchName?: string) {
  if (!currentUser) return

  // 1. Initialize Store Entry
  const storeId = uploadStore.addItem(file.name)
  const { accountId } = getAccountContext()

  if (!accountId) {
    uploadStore.updateItem(storeId, { status: 'error', message: 'Account context missing. Reload page.' })
    return
  }

  try {
    uploadStore.updateItem(storeId, { message: 'Reading file...' })
    
    // 2. Client-Side Processing (Gzip + Read)
    const fileBuffer = await file.arrayBuffer()
    const compressedData = pako.gzip(fileBuffer)

    const workbook = XLSX.read(fileBuffer)
    const worksheet = workbook.Sheets[workbook.SheetNames[0]]

    // --- NEW: AUTO-DETECT BRANCH ---
    const detectedBranchName = detectBranchNameFromSheet(worksheet, format);
    const userBranches = getUserBranches();
    
    // Fuzzy Match: Case-insensitive, ignore extra spaces
    const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');
    const normalizedDetected = normalize(detectedBranchName);

    const matchedBranch = userBranches.find(b => normalize(b.branchName) === normalizedDetected);

    if (!matchedBranch) {
        throw new Error(`Branch "${detectedBranchName}" found in file, but you don't have this branch created. Please go to "My Branches" and create it exactly as named.`);
    }

    const finalBranchId = matchedBranch.branchId;
    const finalBranchName = matchedBranch.branchName; // Use the store's name casing

    uploadStore.updateItem(storeId, { message: `Branch: ${finalBranchName}` });

    // Detect Period
    const { startPeriod, endPeriod } = format === 'MOKA'
      ? getPeriodRangeFromMokaData(worksheet)
      : getPeriodRangeFromEsbData(worksheet)

    const periodRangeText = startPeriod === endPeriod ? startPeriod : `${startPeriod} to ${endPeriod}`
    
    // 3. Create Job Doc
    const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(7)}`
    const jobDocRef = doc(db, `processingJobs`, jobId)
    
    await setDoc(jobDocRef, {
      userId: currentUser.uid,
      accountId: accountId,
      branchId: finalBranchId, // Use auto-detected ID
      jobId: jobId,
      fileName: file.name,
      status: 'preparing',
      createdAt: new Date(),
      format: format,
      periodRange: periodRangeText,
    })

    // Link Store Item to Job ID
    uploadStore.updateItem(storeId, { jobId })
    
    // Start listening
    listenForProcessingStatus(jobId, storeId)

    // 4. Upload to Storage
    const storagePath = `user_uploads/${currentUser.uid}/${jobId}/${file.name}.gz`
    const storageRef = ref(storage, storagePath)

    const metadata = {
      customMetadata: {
        userId: currentUser.uid,
        accountId: accountId,
        branchId: finalBranchId, // Use auto-detected ID
        jobId: jobId,
        format: format,
        startPeriod: startPeriod,
        endPeriod: endPeriod,
        isCompressed: 'true',
      },
    }

    const uploadTask = uploadBytesResumable(storageRef, compressedData, metadata)

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        uploadStore.updateItem(storeId, { 
          status: 'uploading', 
          progress: progress,
          message: 'Uploading...' 
        })
      },
      (error) => {
        console.error(`${format} upload failed:`, error)
        uploadStore.updateItem(storeId, { status: 'error', message: 'Upload Failed' })
        setDoc(jobDocRef, { 'status': 'error', 'progress.message': 'File upload failed.' }, { merge: true })
      },
      async () => {
        await setDoc(jobDocRef, { status: 'uploaded' }, { merge: true })
        uploadStore.updateItem(storeId, { status: 'processing', message: 'Waiting for server...', progress: 0 })
      },
    )
  } catch (error: any) {
    const msg = error.message || 'Unknown error'
    uploadStore.updateItem(storeId, { status: 'error', message: msg })
  }
}

/**
 * Adapted logic for the Modal Upload - now also Auto-Detects Branch
 */
export async function handleModalSalesDataUpload(
  file: File,
  expectedPeriod: string,
  // We ignore the branchId passed from the UI and prefer the file's truth, 
  // OR we can validate that the file matches the expected branch. 
  // For safety in "Quick Upload" (where user clicked a specific row), we should VALIDATE match.
  expectedBranchId: string, 
): Promise<void> {
  if (!currentUser) return

  const storeId = uploadStore.addItem(file.name)
  const { accountId } = getAccountContext()

  if (!accountId) {
    uploadStore.updateItem(storeId, { status: 'error', message: 'Context missing' })
    return
  }

  try {
    uploadStore.updateItem(storeId, { message: 'Verifying file...' })
    
    const fileBuffer = await file.arrayBuffer()
    const compressedData = pako.gzip(fileBuffer)
    const workbook = XLSX.read(fileBuffer)
    const worksheet = workbook.Sheets[workbook.SheetNames[0]]
    
    // 1. Verify Period
    const actualPeriod = getPeriodFromSalesData(worksheet)
    if (actualPeriod !== expectedPeriod) {
      throw new Error(`Period mismatch. Expected ${expectedPeriod}, got ${actualPeriod}`)
    }

    // 2. Verify Branch (ESB format assumed for Quick Upload usually, or auto-detect)
    // We try ESB first, then Moka if that fails/returns nothing
    let detectedBranchName = '';
    try {
        detectedBranchName = detectBranchNameFromSheet(worksheet, 'ESB');
    } catch (e) {
        try { detectedBranchName = detectBranchNameFromSheet(worksheet, 'MOKA'); } catch (e2) { /* ignore */ }
    }

    if (detectedBranchName) {
        const userBranches = getUserBranches();
        const targetBranch = userBranches.find(b => b.branchId === expectedBranchId);
        
        const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');
        if (targetBranch && normalize(detectedBranchName) !== normalize(targetBranch.branchName)) {
             throw new Error(`Branch mismatch. File says "${detectedBranchName}", but you selected "${targetBranch.branchName}".`);
        }
    }

    const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(7)}`
    const jobDocRef = doc(db, `processingJobs`, jobId)
    
    await setDoc(jobDocRef, {
      userId: currentUser.uid,
      accountId: accountId,
      branchId: expectedBranchId,
      jobId: jobId,
      fileName: file.name,
      status: 'preparing',
      createdAt: new Date(),
      format: 'ESB',
      periodRange: actualPeriod,
    })

    uploadStore.updateItem(storeId, { jobId })
    listenForProcessingStatus(jobId, storeId)

    const storagePath = `user_uploads/${currentUser.uid}/${jobId}/${file.name}.gz`
    const storageRef = ref(storage, storagePath)

    const metadata = {
      customMetadata: {
        userId: currentUser.uid,
        period: actualPeriod,
        accountId: accountId,
        branchId: expectedBranchId,
        jobId: jobId,
        format: 'ESB',
        startPeriod: actualPeriod,
        endPeriod: actualPeriod,
        isCompressed: 'true',
      },
    }

    const uploadTask = uploadBytesResumable(storageRef, compressedData, metadata)

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        uploadStore.updateItem(storeId, { status: 'uploading', progress })
      },
      (error) => {
        uploadStore.updateItem(storeId, { status: 'error', message: error.message })
        setDoc(jobDocRef, { 'status': 'error', 'progress.message': error.message }, { merge: true })
      },
      async () => {
        await setDoc(jobDocRef, { status: 'uploaded' }, { merge: true })
        uploadStore.updateItem(storeId, { status: 'processing', message: 'Processing...', progress: 0 })
      }
    )

  } catch (error: any) {
    uploadStore.updateItem(storeId, { status: 'error', message: error.message })
  }
}